import React from "react";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
  CADENCE_OPTIONS,
  cadenceKey,
  cadenceLabel,
  windowLabel,
  audienceLabel,
  scopeLabel,
} from "./taskHubUtils";

// The task definitions with their lifecycle front and center. Draft tasks
// never surface anywhere, so activation is the primary action here.

// Status is information, actions are buttons: the status renders as a quiet
// dot + label while every action gets a border, so the two never read alike.
const STATUS_DOTS = {
  active: "bg-emerald-500",
  draft: "bg-amber-400",
  paused: "bg-slate-300",
};

function StatusLabel({ status }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600">
      <span className={`w-2 h-2 rounded-full ${STATUS_DOTS[status] || STATUS_DOTS.draft}`} />
      {status === "active" ? "Active" : status === "paused" ? "Paused" : "Draft"}
    </span>
  );
}

function ActionButton({ onClick, title, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="text-xs font-semibold text-violet-700 bg-white border border-violet-300 rounded-lg px-3 py-1.5 hover:bg-violet-50 whitespace-nowrap"
    >
      {children}
    </button>
  );
}

const GRID_COLS =
  "grid-cols-[minmax(200px,1.3fr)_minmax(150px,1fr)_minmax(180px,1fr)_170px_180px] sm:grid-cols-[1fr_120px]";

function TaskLibrary({
  planTasks,
  roleNames,
  storeGroups,
  canMutateTask = () => true,
  onNewTask,
  onImportTemplates,
  onEditTask,
  onActivate,
  onPause,
  onResume,
}) {
  const [search, setSearch] = React.useState("");
  const [draftsOnly, setDraftsOnly] = React.useState(false);
  const [cadenceFilter, setCadenceFilter] = React.useState("all");

  const tasks = (planTasks || [])
    .filter((t) =>
      search
        ? (t.name || "").toLowerCase().includes(search.toLowerCase()) ||
          (t.category || "").toLowerCase().includes(search.toLowerCase()) ||
          (t.number != null && String(t.number).includes(search.replace("#", "")))
        : true
    )
    .filter((t) => (draftsOnly ? (t.status || "draft") === "draft" : true))
    .filter((t) => (cadenceFilter === "all" ? true : cadenceKey(t) === cadenceFilter))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const draftCount = (planTasks || []).filter(
    (t) => (t.status || "draft") === "draft"
  ).length;

  return (
    <div>
      {draftCount > 0 && (
        <div className="flex items-center gap-3 bg-violet-50 rounded-xl px-4 py-3 mb-4">
          <div className="flex-1">
            <div className="text-sm font-semibold text-violet-800">
              {draftCount} task{draftCount !== 1 ? "s are" : " is a"} draft
              {draftCount !== 1 ? "s" : ""}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Drafts never appear on anyone's day. Activate a task to start it
              running.
            </div>
          </div>
          <button
            onClick={() => setDraftsOnly((v) => !v)}
            className="text-xs font-semibold border border-violet-200 text-violet-700 rounded-lg px-3 py-1.5 bg-white hover:bg-violet-100 whitespace-nowrap"
          >
            {draftsOnly ? "Show all" : "Show drafts"}
          </button>
        </div>
      )}

      <div className="flex gap-3 items-center mb-4">
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tasks"
          variant="outlined"
          size="small"
          sx={{ width: "288px" }}
          InputProps={{
            sx: {
              backgroundColor: "white",
              borderRadius: "8px",
              fontSize: "14px",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6d28d9",
              },
            },
          }}
        />
        <Select
          value={cadenceFilter}
          onChange={(e) => setCadenceFilter(e.target.value)}
          size="small"
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            height: "40px",
            minWidth: "150px",
            fontSize: "14px",
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
          }}
        >
          <MenuItem value="all">All cadences</MenuItem>
          {CADENCE_OPTIONS.map((o) => (
            <MenuItem key={o.key} value={o.key}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
        <div className="ml-auto flex items-center gap-2">
          {onImportTemplates && (
            <button
              onClick={onImportTemplates}
              className="text-sm font-semibold text-violet-700 bg-white border border-violet-300 rounded-lg px-4 py-2 hover:bg-violet-50 whitespace-nowrap"
            >
              Import templates
            </button>
          )}
          <button
            onClick={onNewTask}
            className="text-sm font-semibold text-white bg-violet-700 rounded-lg px-4 py-2 hover:bg-violet-800 shadow-sm whitespace-nowrap"
          >
            New task
          </button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
        <div className={`grid ${GRID_COLS} gap-3 items-center px-4 py-2.5 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-400`}>
          <span>Task</span>
          <span className="sm:hidden">Audience</span>
          <span className="sm:hidden">Cadence</span>
          <span className="sm:hidden">Window</span>
          <span>Status</span>
        </div>
        {tasks.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <div className="text-sm text-slate-500">
              {search || draftsOnly
                ? "No tasks match."
                : "No tasks yet. Create your first task to get started."}
            </div>
            {!search && !draftsOnly && onImportTemplates && (
              <button
                onClick={onImportTemplates}
                className="mt-3 text-xs font-semibold text-violet-700 bg-white border border-violet-300 rounded-lg px-3 py-1.5 hover:bg-violet-50"
              >
                Browse task templates
              </button>
            )}
          </div>
        ) : (
          tasks.map((t) => {
            const status = t.status || "draft";
            // A legacy quarterly/yearly task has no cadence the system can run;
            // route its lifecycle buttons to the editor so it can be fixed.
            const unsupported =
              !t.cadence && t.type !== "one_off" &&
              ["quarterly", "yearly"].includes(t.repeating_period);
            // The list can include company-wide tasks a manager may see but
            // not edit (the backend rejects those mutations); render them
            // without edit/lifecycle affordances.
            const mutable = canMutateTask(t);
            return (
              <div
                key={t.uuid}
                onClick={() => mutable && onEditTask(t)}
                className={`group grid ${GRID_COLS} gap-3 items-center px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${
                  mutable ? "cursor-pointer" : ""
                }`}
              >
                <span className="text-left min-w-0">
                  <span
                    className={`text-sm font-semibold text-slate-900 truncate block ${
                      mutable ? "group-hover:text-violet-700" : ""
                    }`}
                  >
                    {t.name}
                  </span>
                  <span className="text-[11px] text-slate-400 block mt-0.5">
                    {[t.number ? `#${t.number}` : null, t.category, t.scoring_enabled ? "Scored audit" : null, unsupported ? "needs a cadence" : null]

                      .filter(Boolean)
                      .join(" · ") || " "}
                  </span>
                </span>
                <span className="sm:hidden min-w-0">
                  <span className="inline-flex items-center text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5 truncate max-w-full">
                    {audienceLabel(t, roleNames)}
                  </span>
                  <span className="text-[11px] text-slate-400 block mt-0.5 truncate">
                    {scopeLabel(t, storeGroups?.groups)}
                  </span>
                </span>
                <span className="sm:hidden text-xs font-semibold text-slate-600 truncate">
                  {unsupported ? "Not scheduled" : cadenceLabel(t)}
                </span>
                <span className="sm:hidden text-xs font-semibold text-slate-500 tabular-nums">
                  {windowLabel(t)}
                </span>
                <span className="flex items-center justify-between gap-2">
                  <StatusLabel status={status} />
                  {!mutable ? (
                    <span
                      className="text-[11px] font-semibold text-slate-400 whitespace-nowrap"
                      title="Company-wide tasks can only be changed by admins"
                    >
                      Admin managed
                    </span>
                  ) : status === "active" ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPause(t);
                      }}
                      className="text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:border-slate-400 hover:text-slate-700 whitespace-nowrap"
                    >
                      Pause
                    </button>
                  ) : status === "paused" ? (
                    // Resuming an unsupported legacy period would only be
                    // rejected by set_plan_task_status; route to the editor,
                    // same as the draft branch.
                    <ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        unsupported ? onEditTask(t) : onResume(t);
                      }}
                      title={
                        unsupported
                          ? "Choose a cadence for this task first"
                          : undefined
                      }
                    >
                      {unsupported ? "Fix schedule" : "Resume"}
                    </ActionButton>
                  ) : (
                    <ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        unsupported ? onEditTask(t) : onActivate(t);
                      }}
                      title={
                        unsupported
                          ? "Choose a cadence for this task first"
                          : undefined
                      }
                    >
                      {unsupported ? "Fix schedule" : "Activate"}
                    </ActionButton>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default TaskLibrary;
